// LocalStoreActivate.tsx
import React, { useEffect, useRef, useState } from 'react';
import './LocalStoreActivate.scss';
import { Html5Qrcode } from 'html5-qrcode';
import { Button, Typography, Box, CircularProgress, TextField, Collapse } from '@material-ui/core';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import KeyboardIcon from '@material-ui/icons/Keyboard';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { http, AlertService, useNavigationService, localstorage } from '../fx-core';

const SCANNER_ELEMENT_ID = 'localstore-qr-scanner';

type ScanState = 'idle' | 'scanning' | 'submitting' | 'success' | 'error';

const localStorageUserId = () => localstorage.getItem("userId");

const LocalStoreActivate = (props: any) => {
  const { back } = useNavigationService();
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const [state, setState] = useState<ScanState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [activatedSlotLabel, setActivatedSlotLabel] = useState('');

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
  };

  // Best-effort — the success screen still works fine without this if the
  // lookup fails, since it just adds "for <date, time>" detail.
  const resolveActivatedSlotLabel = (bookingId: number, slotId: number) => {
    if (!bookingId || !slotId) return;
    http.get({ url: `localstore/user/${localStorageUserId()}/approved` })
      .then((res: any) => {
        const stores = res.data?.stores || [];
        const match = stores.find((s: any) => s.id === bookingId);
        const slot = match?.slots?.find((sl: any) => sl.id === slotId);
        if (slot) setActivatedSlotLabel(`${slot.slotDate} · ${slot.slotLabel}`);
      })
      .catch(() => {});
  };

  const submitToken = (token: string) => {
    setState('submitting');

    http.post({ url: `localstore/activate`, data: { qrToken: token } })
      .then((res: any) => {
        if (res.data && res.data.success) {
          setState('success');
          resolveActivatedSlotLabel(res.data.bookingId, res.data.slotId);
        } else {
          setState('error');
          setErrorMessage(errorMessageFor(res.data?.error));
        }
      })
      .catch(() => {
        setState('error');
        setErrorMessage('Something went wrong. Please try again.');
      });
  };

  // The QR now encodes a real URL (e.g. https://sci.localites.in/coimbatore/centralpark/localstore?t=<token>)
  // so that scanning it with a phone camera or Google Lens takes non-app users
  // somewhere useful too. Pull the token back out of the "t" param when present;
  // fall back to treating the scanned text as a bare token for old-format codes.
  const extractToken = (decodedText: string): string => {
    try {
      const url = new URL(decodedText);
      const t = url.searchParams.get('t');
      if (t) return t;
    } catch {
      // not a URL — fall through and treat it as a raw token
    }
    return decodedText;
  };

  const handleScanSuccess = (decodedText: string) => {
    // Stop scanning immediately so the same code isn't submitted multiple times
    // while the activation request is in flight.
    stopScanner();
    submitToken(extractToken(decodedText));
  };

  const handleManualSubmit = () => {
    if (!manualToken.trim()) {
      AlertService.showMsg({ message: 'Paste a QR token first' });
      return;
    }
    submitToken(manualToken.trim());
  };

 const errorMessageFor = (code: string) => {
    switch (code) {
      case 'NoMatchingBooking':
        return "We couldn't find an approved booking for you at this location during the current time slot. Make sure you're scanning during your booked hours.";
      case 'AlreadyActive':
        return 'Your store is already active for this slot.';
      case 'InvalidToken':
        return "That doesn't look like a valid Local Store QR code.";
      case 'NotReady':
        return 'Please add a store name and logo before activating your stall — you can do this from Manage Store → Store Details.';
      default:
        return 'Could not activate your store. Please try again or contact support.';
    }
  };

  const startScanner = () => {
    setState('scanning');
    setErrorMessage('');

    // Give the DOM node a tick to render before Html5Qrcode looks for it
    setTimeout(() => {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = scanner;

      scanner
        .start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => handleScanSuccess(decodedText),
          () => {
            // per-frame "no code found" callback — expected constantly while
            // scanning, intentionally not surfaced as an error
          }
        )
        .catch(() => {
          setState('error');
          setErrorMessage('Could not access the camera. Check camera permissions and try again.');
        });
    }, 100);
  };

  const cancelScan = () => {
    stopScanner();
    setState('idle');
  };

  const retry = () => {
    setState('idle');
    setErrorMessage('');
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <div className="localstore-activate-ui">
      <div className="activate-card">
        <div className="awning-stripe" />

        <div className="activate-header">
          <Typography variant="h5" className="activate-title">Activate Your Store</Typography>
          <Typography className="activate-subtitle">
            Scan the QR code posted at your Local Store location to open your stall for the day.
          </Typography>
        </div>

        <div className="activate-body">
          {(state === 'idle' || state === 'scanning') && (
            <>
              <div className={`scan-stage ${state === 'scanning' ? 'scan-stage--active' : ''}`}>
                {state === 'scanning' ? (
                  <div id={SCANNER_ELEMENT_ID} className="scanner-video" />
                ) : (
                  <>
                    <CameraAltIcon className="stage-icon" />
                    <span className="stage-hint">Your stall's QR code will appear here</span>
                  </>
                )}
                <span className="corner corner-tl" />
                <span className="corner corner-tr" />
                <span className="corner corner-bl" />
                <span className="corner corner-br" />
              </div>

              {state === 'scanning' && (
                <>
                  <Typography className="scan-hint">Point your camera at the QR code…</Typography>
                  <Button className="cancel-scan-btn" onClick={cancelScan}>
                    Cancel
                  </Button>
                </>
              )}

              {state === 'idle' && (
                <>
                  <Button variant="contained" className="primary-btn" onClick={startScanner}>
                    Start Scanning
                  </Button>

                  <div className="manual-toggle">
                    <Button
                      className="toggle-btn"
                      onClick={() => setManualOpen((v) => !v)}
                      aria-expanded={manualOpen}
                    >
                      <KeyboardIcon fontSize="small" />
                      No camera? Enter code manually
                      {manualOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Button>

                    <Collapse in={manualOpen}>
                      <Box className="manual-fields">
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder="Paste QR token here"
                          value={manualToken}
                          onChange={(e) => setManualToken(e.target.value)}
                        />
                        <Button variant="outlined" className="secondary-btn" onClick={handleManualSubmit}>
                          Activate
                        </Button>
                      </Box>
                    </Collapse>
                  </div>
                </>
              )}
            </>
          )}

          {state === 'submitting' && (
            <Box className="submitting-panel">
              <CircularProgress size={32} />
              <Typography>Activating your store…</Typography>
            </Box>
          )}

          {state === 'success' && (
            <div className="state-panel">
              <div className="state-icon-circle state-icon-circle--success">
                <CheckCircleIcon fontSize="inherit" />
              </div>
              <Typography className="state-title">Store Activated</Typography>
              <Typography className="state-message">
                Your Local Store is now visible to residents in this community
                {activatedSlotLabel ? ` for ${activatedSlotLabel}` : ''}.
              </Typography>
              <Button variant="contained" className="done-btn" onClick={() => back()}>
                Done
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div className="state-panel">
              <div className="state-icon-circle state-icon-circle--error">
                <ErrorOutlineIcon fontSize="inherit" />
              </div>
              <Typography className="state-title">Couldn't Activate</Typography>
              <Typography className="state-message">{errorMessage}</Typography>
              <Button variant="outlined" className="secondary-btn" onClick={retry}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocalStoreActivate;